/**
 * lib/auth.tsx — email/password auth + role, fully data-driven.
 * A user is an operator iff an `operators/{email}` doc exists; the doc's
 * `role` (owner | manager | staff) drives what they can see and do.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, type OperatorRole } from './firebase';

interface AuthState {
  user: User | null;
  loading: boolean;
  operator: boolean;
  role: OperatorRole | null;
  signInEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<OperatorRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      const email = u?.email?.toLowerCase();
      if (!email) {
        setRole(null);
        setLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'operators', email));
        setRole(snap.exists() ? ((snap.data().role as OperatorRole) || 'staff') : null);
      } catch {
        setRole(null);
      }
      setLoading(false);
    });
  }, []);

  const value: AuthState = {
    user,
    loading,
    operator: role !== null,
    role,
    signInEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password);
    },
    signOut: async () => {
      await fbSignOut(auth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Which roles may see each portal area. */
export const ACCESS: Record<string, OperatorRole[]> = {
  dashboard: ['owner', 'manager'],
  bookings: ['owner', 'manager', 'staff'],
  clients: ['owner', 'manager'],
  treatments: ['owner', 'manager'],
  invoices: ['owner', 'manager', 'staff'],
  giftcards: ['owner', 'manager'],
  stays: ['owner', 'manager'],
  carhire: ['owner', 'manager'],
  fleet: ['owner', 'manager'],
  images: ['owner', 'manager'],
  settings: ['owner', 'manager'],
  users: ['owner'],
  account: ['owner', 'manager', 'staff'],
};

export function can(role: OperatorRole | null, area: keyof typeof ACCESS): boolean {
  return role != null && ACCESS[area].includes(role);
}
